import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  clearAmplitudeStorage,
  initAmplitude,
  setAmplitudeOptOut,
} from 'src/libs/amp';
import { enableSentryReplay } from 'src/libs/sentry';
import {
  COOKIE_CONSENT_RESET_EVENT,
  getCookieConsent,
  setCookieConsent,
} from 'src/utils/cookieConsent';

// GDPR cookie banner for the public website. Analytics (Amplitude) only
// starts after the visitor accepts; declining keeps the site cookie-free
// apart from strictly necessary ones. The choice is remembered so returning
// visitors are not asked again.
export function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = getCookieConsent();
    if (consent === 'accepted') {
      // Returning visitor who already consented — resume analytics
      initAmplitude();
      enableSentryReplay();
    } else if (consent === null) {
      setIsVisible(true);
    } else {
      // Declined — also remove any identifiers left behind by builds that
      // initialized analytics before consent was introduced
      clearAmplitudeStorage();
    }

    // Footer "Cookie settings" link clears the stored choice and re-opens
    const handleReset = () => setIsVisible(true);
    window.addEventListener(COOKIE_CONSENT_RESET_EVENT, handleReset);
    return () =>
      window.removeEventListener(COOKIE_CONSENT_RESET_EVENT, handleReset);
  }, []);

  const handleAccept = () => {
    setCookieConsent('accepted');
    initAmplitude();
    setAmplitudeOptOut(false);
    enableSentryReplay();
    setIsVisible(false);
  };

  const handleDecline = () => {
    setCookieConsent('declined');
    // Withdrawal after an earlier accept: stop any further collection and
    // remove the identifiers already stored on the device
    setAmplitudeOptOut(true);
    clearAmplitudeStorage();
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1050,
      }}>
      <div className="container pb-3">
        <div className="card rounded-3 shadow-lg border">
          <div className="card-body p-4">
            <h6 className="mb-2">We use cookies 🍪</h6>
            <p className="fs-8 mb-3">
              Our website uses cookies to improve website performance and
              understand how visitors use it. Analytics cookies are only set if
              you accept. See our <Link to="/cookie-policy">Cookie Policy</Link>{' '}
              for details.
            </p>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAccept}>
                Accept
              </button>
              <button
                type="button"
                className="btn btn-outline-light"
                onClick={handleDecline}>
                Decline
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
